import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { permissionsData } from "./permissionsData";
import {
  DEFAULT_FULL_PERMISSIONS,
  getCrudConfig,
  isPermissionApplicable,
  buildPermissionsForApi,
  getAllPermissionKeys,
  canShowPermission,
  INSTALAXMI_KEYS,
  getEmptyPermissions,
} from "./permissionObject";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPermissionsRequest,
  updatePermissionsRequest,
} from "../../../redux/AdminList/adminSlice";
import { keys } from "@mui/system";
import { toast } from "react-toastify";
import auth from "../../../configs/auth";
import { fetchJewellersSidebarRequest } from "../../../redux/JewellerList/JewellerSlice";

const getJewellerId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return String(value._id || value.id || "");
  }
  return String(value);
};

const permissionTypes = ["view", "create", "edit", "delete"];
const MODULE_NAME_COL_WIDTH = 300;
const PERMISSION_COL_WIDTH = 80;

/** Derived from permissionsData (sidebar config) so Jeweller sections always match latest permissions. */
const getJewellerSectionsFromStructure = (structure) =>
  structure.map((item) => ({
    title: item.label,
    keys: item.children?.length ? item.children.map((c) => c.key) : [item.key],
  }));

const JEWELLER_SECTIONS = getJewellerSectionsFromStructure(permissionsData);

/** Derived from permissionsData (sidebar config) so Instalaxmi sections always match latest permissions. */
const getInstalaxmiSectionsFromStructure = (structure) => {
  const sections = [];
  
  // Add instalaxmiPermission as a special section
  const instalaxmiPermissionItem = structure.find(
    (item) => item.key === "instalaxmiPermission"
  );
  if (instalaxmiPermissionItem) {
    sections.push({
      title: "Instalaxmi Permission",
      titleKey: "pages.instalaxmiPermission",
      keys: ["instalaxmiPermission"],
    });
  }
  
  // Add other sections that contain Instalaxmi keys
  structure.forEach((item) => {
    if (item.key === "instalaxmiPermission") {
      // Already added above
      return;
    }
    
    if (item.children && item.children.length > 0) {
      // Check if any child keys are Instalaxmi keys
      const instalaxmiChildKeys = item.children
        .map((c) => c.key)
        .filter((k) => INSTALAXMI_KEYS.has(k));
      
      if (instalaxmiChildKeys.length > 0) {
        sections.push({
          title: item.label,
          keys: instalaxmiChildKeys,
        });
      }
    } else if (INSTALAXMI_KEYS.has(item.key)) {
      // Direct key match (e.g., "jeweller")
      sections.push({
        title: item.label,
        keys: [item.key],
      });
    }
  });
  
  return sections;
};

const INSTALAXMI_SECTIONS = getInstalaxmiSectionsFromStructure(permissionsData);

/** Keys to exclude from Jeweller section (all Instalaxmi keys). */
const JEWELLER_EXCLUDED_KEYS = INSTALAXMI_KEYS;

/** Jeweller sections with JEWELLER_EXCLUDED_KEYS removed (not shown in Jeweller block). */
const JEWELLER_SECTIONS_FILTERED = JEWELLER_SECTIONS.map((section) => ({
  ...section,
  keys: section.keys.filter((k) => !JEWELLER_EXCLUDED_KEYS.has(k)),
})).filter((section) => section.keys.length > 0);

const PermissionsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = location.state?.userId || storedUser?._id;

  const { permissions: reduxPermissions, permissionsLoading } = useSelector(
    (state) => state.admin
  );

  const isCreateFlow = !location.state?.userId;

  const mergeWithFullPermissionKeys = (loaded) => {
    const allKeys = getAllPermissionKeys();
    const base = {};
    allKeys.forEach((key) => {
      base[key] = {
        view: !!loaded[key]?.view,
        create: !!loaded[key]?.create,
        edit: !!loaded[key]?.edit,
        delete: !!loaded[key]?.delete,
      };
    });
    return base;
  };

  const [permissions, setPermissions] = useState(() => getEmptyPermissions());
  const [role, setRole] = useState("");
  const [selectedJewellerId, setSelectedJewellerId] = useState(() => {
    return sessionStorage.getItem(auth.selectedJeweller) || "";
  });
  const jewellerID = getJewellerId(storedUser?.jeweller);
  const selectedId = getJewellerId(selectedJewellerId);

  const jewellersSidebar = useSelector(
    (state) => state.jeweller.jewellersSidebar || []
  );
  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest());
  }, [dispatch]);
  const instalaxmiJeweller = jewellersSidebar.find(
    (item) => item.jewellerName?.toLowerCase() === "instalaxmi"
  );
  const instalaxmiJewellerId = getJewellerId(
    instalaxmiJeweller?.id || instalaxmiJeweller?._id
  );
  const isLoggedInInstalaxmiUser =
    storedUser?.jewellerName?.toLowerCase() === "instalaxmi" &&
    jewellerID &&
    selectedId === jewellerID;
  const showInstalaxmiSection =
    !!selectedId &&
    (isLoggedInInstalaxmiUser ||
      (!!instalaxmiJewellerId && selectedId === instalaxmiJewellerId));

  const createdRoles = reduxPermissions?.createdRoles || [];
  const [createRoles, setCreateRoles] = useState([]);
  useEffect(() => {
    const finalJewellerId = selectedId || jewellerID;
    if (createdRoles.length > 0) {
      const roleNames = createdRoles
        .filter((r) => getJewellerId(r.jeweller) === finalJewellerId)
        .map((r) => r.role)
        .filter((r) => r && r.trim() !== "");
      setCreateRoles(roleNames);
    } else {
      setCreateRoles([]);
    }
  }, [createdRoles, selectedJewellerId, jewellerID]);

  // When roles exist: select first role. When no roles: clear role and set all permissions false.
  useEffect(() => {
    if (createRoles.length > 0) {
      setRole((prev) =>
        !prev || !createRoles.includes(prev) ? createRoles[0] : prev
      );
    } else {
      setRole("");
      setPermissions(getEmptyPermissions());
    }
  }, [createRoles]);

  useEffect(() => {
    if (role && reduxPermissions?.createdRoles?.length > 0) {
      const selectedRole = reduxPermissions.createdRoles.find(
        (r) => r.role === role
      );
      if (selectedRole?.permissions) {
        const merged = mergeWithFullPermissionKeys(selectedRole.permissions);
        setPermissions(merged);
      } else {
        setPermissions(getEmptyPermissions());
      }
    }
  }, [role, reduxPermissions.createdRoles]);

  const [roleError, setRoleError] = useState("");

  const handleRoleChange = (e) => {
    const value = e.target.value;

    if (value.trim().toLowerCase() === "god") {
      setRoleError("Invalid Role");
    } else {
      setRoleError("");
      setRole(value);
    }
  };

  // Helper: flatten nested keys into leaf keys only
  const flattenPermissions = (nestedPerms) => {
    const flat = {};
    for (const key in nestedPerms) {
      const parts = key.split(".");
      const leafKey = parts[parts.length - 1];
      flat[leafKey] = {
        ...(flat[leafKey] || {}),
        ...nestedPerms[key],
      };
    }
    return flat;
  };

  const finalJewellerId =
    selectedJewellerId && selectedJewellerId.trim() !== ""
      ? selectedJewellerId
      : jewellerID;

  useEffect(() => {
    if (userId) {
      dispatch(
        fetchPermissionsRequest({ userId, jewellerId: finalJewellerId })
      );
    }
  }, [userId, finalJewellerId, dispatch]);

  useEffect(() => {
    if (
      reduxPermissions.filteredPermissions &&
      Object.keys(reduxPermissions.filteredPermissions).length > 0
    ) {
      const flatPermissions = flattenPermissions(
        reduxPermissions.filteredPermissions
      );
      const merged = mergeWithFullPermissionKeys(flatPermissions);
      setPermissions(merged);
    }
  }, [reduxPermissions.filteredPermissions]);

  const getAllKeys = (module, parentKey = "") => {
    const currentKey = parentKey ? `${parentKey}.${module.key}` : module.key;
    let keys = [currentKey];
    if (module.children) {
      for (let child of module.children) {
        keys = keys.concat(getAllKeys(child, currentKey));
      }
    }
    return keys;
  };

  const handleToggle = (key, type, value, module) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      const leafKey = module.key;

      updated[leafKey] = {
        ...(updated[leafKey] || {}),
        [type]: value,
      };

      if (["create", "edit", "delete"].includes(type) && value) {
        updated[leafKey].view = true;
      }
      if (type === "view" && !value) {
        updated[leafKey].create = false;
        updated[leafKey].edit = false;
        updated[leafKey].delete = false;
      }

      return updated;
    });
  };

  const includeOnlyInstalaxmiModules = (modules) =>
    modules
      .map((mod) => {
        const children = mod.children
          ? includeOnlyInstalaxmiModules(mod.children)
          : [];

        // Include module if it's an Instalaxmi key or has Instalaxmi children
        const isInstalaxmiKey = INSTALAXMI_KEYS.has(mod.key);
        if (isInstalaxmiKey || children.length > 0) {
          return {
            ...mod,
            children: children.length > 0 ? children : undefined,
          };
        }
        return null;
      })
      .filter(Boolean);

  const filterModulesForJeweller = (modules, parentKey = "") =>
    modules
      .flatMap((mod) => {
        const currentKey = parentKey ? `${parentKey}.${mod.key}` : mod.key;

        if (JEWELLER_EXCLUDED_KEYS.has(currentKey)) return [];

        const children = mod.children
          ? filterModulesForJeweller(mod.children, currentKey)
          : undefined;

        return [{ ...mod, children }];
      })
      .filter(Boolean);

  const isModuleActive = (mod) => {
    if (!mod.children || mod.children.length === 0) {
      return !!permissions[mod.key]?.view;
    }
    return mod.children.some(isModuleActive);
  };

  const renderRows = (modules, parentKey = "", level = 0) =>
    modules.flatMap((mod) => {
      const currentKey = parentKey ? `${parentKey}.${mod.key}` : mod.key;
      const hasChildren = mod.children && mod.children.length > 0;

      const parentRow = (
        <TableRow key={currentKey}>
          <TableCell sx={{ pl: `${level * 2 + 2}px` }}>
            <Typography sx={{ ml: 1 }}>{mod.label}</Typography>
          </TableCell>
          {permissionTypes.map((type) => {
            if (!hasChildren && !isPermissionApplicable(mod.key, type)) {
              return (
                <TableCell key={type} align="center">
                  —
                </TableCell>
              );
            }
            return (
              <TableCell key={type} align="center">
                <Switch
                  size="small"
                  checked={
                    type === "view"
                      ? hasChildren
                        ? isModuleActive(mod)
                        : permissions[mod.key]?.view || false
                      : permissions[mod.key]?.[type] || false
                  }
                  onChange={(e) =>
                    handleToggle(mod.key, type, e.target.checked, mod)
                  }
                  disabled={hasChildren && type !== "view"}
                />
              </TableCell>
            );
          })}
        </TableRow>
      );

      const childRows = hasChildren
        ? renderRows(mod.children, currentKey, level + 1)
        : [];

      return [parentRow, ...childRows];
    });

  const filterEmptyPermissions = (perms) => {
    const filtered = {};
    for (const [key, value] of Object.entries(perms)) {
      const trueOnly = Object.fromEntries(
        Object.entries(value).filter(([_, v]) => v === true)
      );
      if (Object.keys(trueOnly).length > 0) {
        filtered[key] = trueOnly;
      }
    }
    return filtered;
  };

  const { updateSuccess } = useSelector((state) => state.admin);
  const prevUpdateSuccess = useRef(updateSuccess);

  // useEffect(() => {
  //   if (!prevUpdateSuccess.current && updateSuccess) {
  //     navigate("/AdminList");
  //   }
  //   prevUpdateSuccess.current = updateSuccess;
  // }, [updateSuccess, navigate]);

  useEffect(() => {
    if (!prevUpdateSuccess.current && updateSuccess) {
      toast.success("Permissions updated successfully!");
      window.location.reload();
      //window.location.href = "/AdminList";
    }
    prevUpdateSuccess.current = updateSuccess;
  }, [updateSuccess]);

  const gatherLeafKeys = (mods) => {
    let keys = [];
    mods.forEach((mod) => {
      keys.push(mod.key);
      if (mod.children && mod.children.length > 0) {
        keys = keys.concat(gatherLeafKeys(mod.children));
      }
    });
    return keys;
  };

  const filterPermissionsByModules = (perms, modules) => {
    const allowedKeys = new Set(gatherLeafKeys(modules));
    const filtered = {};
    for (const [key, value] of Object.entries(perms)) {
      if (allowedKeys.has(key)) {
        filtered[key] = value;
      }
    }
    return filtered;
  };
  const [user, setUser] = useState(null);
  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error("Error parsing storedUser:", error);
      }
    }
  }, []);

  const handleSubmit = (type) => {
    // Get Jeweller modules (always visible)
    const jewellerModules = filterModulesForJeweller(permissionsData);
    
    // Check if Instalaxmi permissions are visible (same condition as rendering)
    const isInstalaxmiVisible = showInstalaxmiSection && canShowInstalaxmiPermissions();
    
    if (isInstalaxmiVisible) {
      // Both Jeweller and Instalaxmi permissions are visible - save both
      const instalaxmiModules = includeOnlyInstalaxmiModules(permissionsData);
      
      // Combine leaf keys from both module sets
      const jewellerKeys = new Set(gatherLeafKeys(jewellerModules));
      const instalaxmiKeys = new Set(gatherLeafKeys(instalaxmiModules));
      const allKeys = new Set([...jewellerKeys, ...instalaxmiKeys]);
      
      // Filter permissions to include only visible keys (both types)
      const filteredPermissions = {};
      for (const [key, value] of Object.entries(permissions)) {
        if (allKeys.has(key)) {
          filteredPermissions[key] = value;
        }
      }
      
      const permissionsForApi = buildPermissionsForApi(filteredPermissions);

      if (userId) {
        dispatch(
          updatePermissionsRequest({
            jeweller: finalJewellerId,
            userId,
            permissions: permissionsForApi,
            role: role.trim().toLowerCase(),
          })
        );
      } else {
        navigate("/AdminList", {
          state: {
            openCreateAdmin: true,
            permissions: permissionsForApi,
            type,
          },
        });
      }
      return;
    }

    // Only Jeweller permissions visible - save only Jeweller permissions
    const filteredPermissions = filterPermissionsByModules(
      permissions,
      jewellerModules
    );
    const permissionsForApi = buildPermissionsForApi(filteredPermissions);

    if (userId) {
      dispatch(
        updatePermissionsRequest({
          jeweller: finalJewellerId,
          userId,
          permissions: permissionsForApi,
          role: role.trim().toLowerCase(),
        })
      );
    } else {
      navigate("/AdminList", {
        state: {
          openCreateAdmin: true,
          permissions: permissionsForApi,
          type,
        },
      });
    }
  };

  const enableAllPermissions = (modules, parentKey = "") => {
    setPermissions((prev) => {
      const updated = { ...prev };
      const keyMeta = [];

      const gatherKeys = (mods, parent) => {
        mods.forEach((mod) => {
          const currentKey = parent ? `${parent}.${mod.key}` : mod.key;
          if (!mod.children || mod.children.length === 0) {
            keyMeta.push({ key: mod.key, crud: getCrudConfig(mod.key) });
          } else {
            gatherKeys(mod.children, currentKey);
          }
        });
      };

      gatherKeys(modules, parentKey);

      const allEnabled = keyMeta.every(({ key, crud }) => {
        if (crud.read && !updated[key]?.view) return false;
        if (crud.create && !updated[key]?.create) return false;
        if (crud.update && !updated[key]?.edit) return false;
        if (crud.delete && !updated[key]?.delete) return false;
        return true;
      });

      const newValue = !allEnabled;

      keyMeta.forEach(({ key, crud }) => {
        updated[key] = {};
        if (crud.read) updated[key].view = newValue;
        if (crud.create) updated[key].create = newValue;
        if (crud.update) updated[key].edit = newValue;
        if (crud.delete) updated[key].delete = newValue;
      });

      return updated;
    });
  };

  const [email, setEmail] = useState("");

  useEffect(() => {
    const storedEmail = localStorage.getItem("userEmail");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  const [userPermissions, setUserPermissions] = useState();

  useEffect(() => {
    const storedPerm = localStorage.getItem("userPermissions");
    if (storedPerm) {
      try {
        setUserPermissions(JSON.parse(storedPerm));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  // Check if Instalaxmi permissions should be shown:
  // 1. User has instalaxmiPermission view permission
  // 2. Logged in user's jeweller ID matches selected jeweller ID
  const canShowInstalaxmiPermissions = () => {
    if (!userPermissions) return false;
    const hasInstalaxmiPermission = canShowPermission(
      userPermissions,
      "instalaxmiPermission",
      "view"
    );
    const jewellerIdsMatch = !!jewellerID && !!selectedId && jewellerID === selectedId;
    return hasInstalaxmiPermission && jewellerIdsMatch;
  };

  const enableAllPermissionsForJeweller = () => {
    const allModules = filterModulesForJeweller(permissionsData);
    const flatModules = {};
    const flatten = (mods) => {
      mods.forEach((mod) => {
        flatModules[mod.key] = mod;
        if (mod.children) flatten(mod.children);
      });
    };
    flatten(allModules);

    const jewellerModules = JEWELLER_SECTIONS_FILTERED
      .flatMap((section) => section.keys)
      .map((k) => flatModules[k])
      .filter(Boolean);

    enableAllPermissions(jewellerModules);
  };

  const enableAllPermissionsForInstalaxmi = () => {
    const allModules = includeOnlyInstalaxmiModules(permissionsData);
    const flatModules = {};
    const flatten = (mods) => {
      mods.forEach((mod) => {
        flatModules[mod.key] = mod;
        if (mod.children) flatten(mod.children);
      });
    };
    flatten(allModules);

    const instalaxmiModules = INSTALAXMI_SECTIONS
      .flatMap((section) => section.keys)
      .map((k) => flatModules[k])
      .filter(Boolean);

    enableAllPermissions(instalaxmiModules);
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflowY: "auto", p: 2 }}>
        <Box
          sx={{
            maxWidth: "100%",
            margin: "auto",
            mt: 0,
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            p: 3,
          }}
        >
          <Typography variant="h5" mb={3}>
            Manage Permissions {permissionsLoading && "(Loading...)"}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Create New Role
            </Typography>

            <input
              type="text"
              placeholder="Create New Role"
              onChange={(e) => {
                setRole(e.target.value);
              }}
              value={role}
              style={{
                width: "30%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            />

            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              style={{
                width: "30%",
                padding: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                fontSize: "14px",
                marginBottom: "10px",
                display: "block",
              }}
            >
              <option value="">Select Existing Role</option>
              {createRoles
                .filter((r) => r.toLowerCase() !== "god")
                .map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
            </select>

            <datalist id="rolesList">
              {createRoles
                .filter((r) => r.toLowerCase() !== "god")
                .map((r) => (
                  <option key={r} value={r} />
                ))}
            </datalist>

            {roleError && (
              <Typography sx={{ color: "red", mt: 1, fontSize: "12px" }}>
                {roleError}
              </Typography>
            )}
          </Box>
          {/* Instalaxmi */}
          {showInstalaxmiSection &&
            canShowInstalaxmiPermissions() && (
              <>
                <Typography variant="h6" mt={4} mb={2}>
                  Instalaxmi
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={enableAllPermissionsForInstalaxmi}
                  sx={{ mb: 1 }}
                >
                  Enable All
                </Button>

                {(() => {
                  const getModulesByKeys = (keys) => {
                    const allModules =
                      includeOnlyInstalaxmiModules(permissionsData);
                    const flatModules = {};
                    const flatten = (mods) => {
                      mods.forEach((mod) => {
                        flatModules[mod.key] = mod;
                        if (mod.children) flatten(mod.children);
                      });
                    };
                    flatten(allModules);
                    return keys.map((k) => flatModules[k]).filter(Boolean);
                  };

                  const renderSectionTable = (section, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {section.titleKey ? t(section.titleKey) : section.title}
                      </Typography>
                      <Paper variant="outlined" sx={{ mb: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  minWidth: MODULE_NAME_COL_WIDTH,
                                  maxWidth: MODULE_NAME_COL_WIDTH,
                                }}
                              >
                                Module
                              </TableCell>
                              {permissionTypes.map((type) => (
                                <TableCell
                                  key={type}
                                  align="center"
                                  sx={{
                                    fontWeight: "bold",
                                    minWidth: PERMISSION_COL_WIDTH,
                                    maxWidth: PERMISSION_COL_WIDTH,
                                  }}
                                >
                                  {type.toUpperCase()}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {renderRows(getModulesByKeys(section.keys))}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Box>
                  );

                  return INSTALAXMI_SECTIONS.map(renderSectionTable);
                })()}
              </>
            )}

          {/* Jeweller */}
          <Typography variant="h6" mt={4} mb={2}>
            Jeweller
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={enableAllPermissionsForJeweller}
            sx={{ mb: 1 }}
          >
            Enable All
          </Button>

          {(() => {
            const getModulesByKeys = (keys) => {
              const allModules = filterModulesForJeweller(permissionsData);
              const flatModules = {};
              const flatten = (mods) => {
                mods.forEach((mod) => {
                  flatModules[mod.key] = mod;
                  if (mod.children) flatten(mod.children);
                });
              };
              flatten(allModules);
              return keys.map((k) => flatModules[k]).filter(Boolean);
            };

            return JEWELLER_SECTIONS_FILTERED.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  {section.titleKey ? t(section.titleKey) : section.title}
                </Typography>
                <Paper variant="outlined" sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            minWidth: MODULE_NAME_COL_WIDTH,
                            maxWidth: MODULE_NAME_COL_WIDTH,
                          }}
                        >
                          Module
                        </TableCell>
                        {permissionTypes.map((type) => (
                          <TableCell
                            key={type}
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              minWidth: PERMISSION_COL_WIDTH,
                              maxWidth: PERMISSION_COL_WIDTH,
                            }}
                          >
                            {type.toUpperCase()}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {renderRows(getModulesByKeys(section.keys))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            ));
          })()}

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => handleSubmit(userId ? "edit" : "create")}
              sx={{
                textTransform: "none",
                background: userId
                  ? "linear-gradient(90deg, #28A745 0%, #218838 76.93%)"
                  : "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                px: 4,
                py: 1.5,
              }}
            >
              {userId ? "Update Permissions" : "Submit Permissions"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PermissionsPage;
